import React, { useState } from 'react';
import classNames from 'classnames';
import { action, makeObservable, observable, autorun } from 'mobx';
import { observer } from 'mobx-react';

interface PopupPropsType {
    className?: string;
    children?: React.ReactNode;
    priority?: number;
    title?: string;
    closeButton?: boolean;
    active?: boolean;
    onClose?: () => void;
    overlayClose?: boolean;
}

class PopupState {
    @observable public isActive: boolean;
    private popupElement: HTMLDivElement | null = null;
    private autorunDisposer: (() => void) | null = null;

    public constructor(initialActive: boolean) {
        this.isActive = initialActive;
        makeObservable(this);
    }

    @action public setIsActive = (active: boolean): void => {
        this.isActive = active;
    };

    public setPopupElementRef = (element: HTMLDivElement | null): void => {
        if (this.popupElement !== element) {
            this.popupElement = element;

            if (element !== null && this.autorunDisposer === null) {
                this.setupAutoFocus();
            } else if (element === null && this.autorunDisposer !== null) {
                this.dispose();
            }
        }
    };

    private setupAutoFocus = (): void => {
        this.autorunDisposer = autorun(() => {
            if (this.isActive && this.popupElement !== null) {
                this.popupElement.focus();
            }
        });
    };

    public dispose = (): void => {
        if (this.autorunDisposer !== null) {
            this.autorunDisposer();
            this.autorunDisposer = null;
        }
    };

    @action public close = (onClose?: () => void): void => {
        if (onClose !== undefined) {
            onClose();
        }
        this.setIsActive(false);
    };

    @action public handleClose = (e: React.MouseEvent, onClose?: () => void): void => {
        e.preventDefault();
        e.stopPropagation();
        this.close(onClose);
    };

    @action public handleOverlayClick = (e: React.MouseEvent, overlayClose: boolean, onClose?: () => void): void => {
        if (overlayClose === true) {
            this.handleClose(e, onClose);
        }
    };

    @action public handleKeyDown = (e: React.KeyboardEvent, onClose?: () => void): void => {
        if (e.key === 'Escape') {
            this.close(onClose);
        }
    };

    @action public ignoreClick = (e: React.MouseEvent): void => {
        e.stopPropagation();
    };
}

export const Popup = observer(
    ({
        className,
        children,
        priority = 0,
        title,
        closeButton = true,
        active = true,
        onClose,
        overlayClose = false,
    }: PopupPropsType) => {
        const [popupState] = useState(() => new PopupState(active));

        if (popupState.isActive !== active) {
            popupState.setIsActive(active);
        }

        const popupClassName = classNames([className, 'popup-overlay', { active: popupState.isActive }]);

        const zIndex = 10000 + priority;

        let titleEl: React.ReactNode | undefined;
        if (typeof title === 'string' && title.length > 0) {
            let closeButtonEl: React.ReactNode | undefined;
            if (closeButton === true) {
                closeButtonEl = (
                    <a
                        href='#'
                        onClick={(e) => popupState.handleClose(e, onClose)}
                        className='popup-close'
                    >
                        &times;
                    </a>
                );
            }

            titleEl = (
                <header className='popup-title'>
                    {title}
                    {closeButtonEl}
                </header>
            );
        }

        return (
            <div
                ref={popupState.setPopupElementRef}
                className={popupClassName}
                style={{ zIndex }}
                onClick={(e) => popupState.handleOverlayClick(e, overlayClose, onClose)}
                onKeyDown={(e) => popupState.handleKeyDown(e, onClose)}
                tabIndex={-1}
            >
                <div className='popup-wrapper'>
                    <div
                        className='popup'
                        onClick={popupState.ignoreClick}
                    >
                        {titleEl}
                        <div className='popup-body'>{children}</div>
                    </div>
                </div>
            </div>
        );
    }
);
